// /**
//  * Users - list
//  */

// React
import React, {useContext} from 'react';

// Context
import AuthContext from '../../../authContext';

// UI
import theme from '../../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {mdiClose, mdiCheck, mdiChevronDown, mdiChevronUp} from '@mdi/js';

// Components
import Can from '../../../components/Can';

// Helper
import {checkMatchMedia} from '../../../utils/helpers';

// Type
import {ProviderList} from '../interfaces';

type Data = ProviderList;
type Props = {
  data: ProviderList[];
  handleOpenConfirmDisable: (id: number) => void;
  handleOpenConfirmActive: (id: number) => void;
  handleOpen: (id: number, type: string) => void;
  changeStatus: any;
};

type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      textAlign: 'left',
      padding: '10px 15px',
      backgroundColor: '#D5DADD !important',
      color: '#212121',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    body: {
      textAlign: 'left',
      padding: '10px 15px',
      borderRadius: '0',
      fontSize: 14,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
    hover: {
      '&:hover': {
        backgroundColor: '#F8F9FA !important',
      },
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof never>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const headCells: HeadCell[] = [
  {
    id: 'name',
    label: 'نام',
  },
  {
    id: 'id',
    label: 'شناسه',
  },
  {
    id: 'code',
    label: 'کد تامین‌کننده',
  },
  {id: 'productCount', label: 'تعداد کالا'},
  {id: 'orderCount', label: 'تعداد سفارش'},
];

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  const {order, orderBy, onRequestSort} = props;
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell />
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={0.7}
                  style={{marginRight: '8px'}}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              {headCell.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const {handleOpenConfirmDisable, handleOpenConfirmActive, handleOpen} = props;

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />

          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow className={classes.hover} key={index}>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      <span
                        className={classes.dot}
                        style={{
                          backgroundColor:
                            row.status !== 'ACTIVE'
                              ? theme.palette.error.main
                              : theme.palette.green.main,
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">{row.name}</StyledTableCell>
                    <StyledTableCell align="center">{row.id}</StyledTableCell>
                    <StyledTableCell align="center">{row.code}</StyledTableCell>
                    <StyledTableCell align="center">
                      {row.productCount}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.orderCount}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Can
                        role={authContext.user.role}
                        perform="providers:disable"
                        yes={() => (
                          <>
                            {row.status !== 'ACTIVE' ? (
                              <IconButton
                                aria-label="active"
                                title=" فعال کردن تامین کننده "
                                size="small"
                                onClick={() => handleOpenConfirmActive(row.id)}
                              >
                                <Icon
                                  path={mdiCheck}
                                  size={1}
                                  color={theme.palette.green.main}
                                />
                              </IconButton>
                            ) : (
                              <IconButton
                                aria-label="cancel"
                                title="غیر فعال کردن تامین کننده "
                                size="small"
                                onClick={() => handleOpenConfirmDisable(row.id)}
                              >
                                <Icon
                                  path={mdiClose}
                                  size={1}
                                  color={theme.palette.error.main}
                                />
                              </IconButton>
                            )}
                          </>
                        )}
                      />
                    </StyledTableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
